import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FeesApi } from '@/services/fees.api';
import { AcademicClassesApi } from '@/services/academic.api';
import { UploadApi } from '@/services/school.api';
import { Field, Input, Select } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useTerminology } from '@/hooks/useTerminology';

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}

/**
 * Shared Class -> Student -> Fee cascade for both quick-create modals below —
 * same pickers (`FeesApi.studentsPicker`/`feeStructuresPicker`) already used
 * by FeeDemandFormPage.tsx. No Section step, matching the reference forms
 * (Class + Student is enough to scope the picker; Section isn't shown there).
 */
function useCascadePickers() {
  const [classId, setClassId] = useState('');
  const [studentId, setStudentId] = useState('');
  const [feeStructureId, setFeeStructureId] = useState('');

  const { data: classesPage } = useQuery({
    queryKey: ['academic-classes', 'all-for-fees-quick-create'],
    queryFn: () => AcademicClassesApi.list({ limit: 100 }),
  });
  const classes = classesPage?.items ?? [];

  const { data: students = [] } = useQuery({
    queryKey: ['fees', 'students-picker', classId],
    queryFn: () => FeesApi.studentsPicker({ classId }),
    enabled: !!classId,
  });

  const { data: feeStructures = [] } = useQuery({
    queryKey: ['fees', 'fee-structures-picker', classId],
    queryFn: () => FeesApi.feeStructuresPicker(classId),
    enabled: !!classId,
  });

  const reset = () => {
    setClassId('');
    setStudentId('');
    setFeeStructureId('');
  };

  return { classId, setClassId, studentId, setStudentId, feeStructureId, setFeeStructureId, classes, students, feeStructures, reset };
}

/** "Create Fees Collection" — Class -> Student -> Fee -> Amount only, no payment. */
export function CreateFeesCollectionModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const term = useTerminology();
  const p = useCascadePickers();
  const [amount, setAmount] = useState('');

  useEffect(() => {
    if (open) {
      p.reset();
      setAmount('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const save = useMutation({
    mutationFn: () =>
      FeesApi.createDemand({ studentId: p.studentId, feeStructureId: p.feeStructureId, amount: Number(amount) }),
    onSuccess: () => {
      onCreated();
      onClose();
    },
  });

  const valid = !!p.studentId && !!p.feeStructureId && Number(amount) > 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create Fees Collection"
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" disabled={save.isPending || !valid} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Create Fees Collection'}
          </button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label={term.level} required>
          <Select value={p.classId} onChange={(e) => { p.setClassId(e.target.value); p.setStudentId(''); p.setFeeStructureId(''); }}>
            <option value="">Select {term.level}</option>
            {p.classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Student" required>
          <Select value={p.studentId} onChange={(e) => p.setStudentId(e.target.value)} disabled={!p.classId}>
            <option value="">Select Student</option>
            {p.students.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.admissionNo})
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Fee" required>
          <Select value={p.feeStructureId} onChange={(e) => p.setFeeStructureId(e.target.value)} disabled={!p.classId}>
            <option value="">Select Fee</option>
            {p.feeStructures.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Amount" required>
          <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        {save.error && (
          <div className="sm:col-span-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(save.error)}</div>
        )}
      </div>
    </Modal>
  );
}

/**
 * "Create Single Fees Collection" — Class -> Student -> Fee -> Amount, plus
 * discount + payment mode/date/reference/image, one "Pay" submit. Hits the
 * same `createDemand()` as above with the payment fields filled in — the
 * backend already creates the demand and records its one inline payment in
 * a single transaction, and defaults `paidAmount` to the full net amount
 * (amount - discount) when payment fields are given but `paidAmount` isn't
 * — i.e. "paid in full today", which is exactly this form's one-click intent.
 */
export function CreateSingleFeesCollectionModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const term = useTerminology();
  const p = useCascadePickers();
  const [amount, setAmount] = useState('');
  const [discount, setDiscount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [mode, setMode] = useState('Cash');
  const [reference, setReference] = useState('');
  const [image, setImage] = useState<string | undefined>(undefined);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) {
      p.reset();
      setAmount('');
      setDiscount('');
      setDate(new Date().toISOString().slice(0, 10));
      setMode('Cash');
      setReference('');
      setImage(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const save = useMutation({
    mutationFn: () =>
      FeesApi.createDemand({
        studentId: p.studentId,
        feeStructureId: p.feeStructureId,
        amount: Number(amount),
        discountAmount: discount ? Number(discount) : undefined,
        paymentDate: date,
        paymentMode: mode,
        paymentReference: reference || undefined,
        paymentImage: image,
      }),
    onSuccess: () => {
      onCreated();
      onClose();
    },
  });

  const valid = !!p.studentId && !!p.feeStructureId && Number(amount) > 0 && !!date && !!mode;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create Single Fees Collection"
      description="Creates the demand and records the payment (and discount, if any) in one step — for a walk-in payment with no pre-generated demand."
      size="lg"
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" disabled={save.isPending || !valid} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Pay'}
          </button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label={term.level} required>
          <Select value={p.classId} onChange={(e) => { p.setClassId(e.target.value); p.setStudentId(''); p.setFeeStructureId(''); }}>
            <option value="">Select {term.level}</option>
            {p.classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Student" required>
          <Select value={p.studentId} onChange={(e) => p.setStudentId(e.target.value)} disabled={!p.classId}>
            <option value="">Select Student</option>
            {p.students.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.admissionNo})
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Fee" required>
          <Select value={p.feeStructureId} onChange={(e) => p.setFeeStructureId(e.target.value)} disabled={!p.classId}>
            <option value="">Select Fee</option>
            {p.feeStructures.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Amount" required>
          <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <Field label="Discount amount">
          <Input type="number" step="0.01" value={discount} onChange={(e) => setDiscount(e.target.value)} placeholder="Optional" />
        </Field>
        <Field label="Payment Date" required>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
        <Field label="Payment Mode" required>
          <Select value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="Cash">Cash</option>
            <option value="Online">Online</option>
            <option value="Cheque">Cheque</option>
            <option value="Card">Card</option>
          </Select>
        </Field>
        <Field label="Payment Reference">
          <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Optional" />
        </Field>
        <Field label="Payment Image" className="sm:col-span-2">
          <input
            type="file"
            accept="image/*"
            disabled={uploading}
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              setUploading(true);
              try {
                const { url } = await UploadApi.upload(f);
                setImage(url);
              } finally {
                setUploading(false);
              }
            }}
          />
          {uploading && <span className="ml-2 text-xs text-slate-400">Uploading…</span>}
          {image && !uploading && <span className="ml-2 text-xs text-green-600">Uploaded</span>}
        </Field>
        {save.error && (
          <div className="sm:col-span-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(save.error)}</div>
        )}
      </div>
    </Modal>
  );
}
